import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideHeader,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";
import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AnchorNavigation, { mappedAnchorLink } from "@sonic/Widgets/Common/AnchorNavigation";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface HomePageStyles {
    page?: MobiusPageProps;
    gridContainerStyle?: InjectableCss;
    stickyNav?: InjectableCss;
}

export const homePageStyles: HomePageStyles = {
    page: {
        fullWidth: [true, true, true, true, true],
        css: css`
            overflow: unset !important;
        `,
    },
    gridContainerStyle: {
        css: css`
            margin: auto;
            margin-top: 20px;
            margin-bottom: -45px;
            overflow: hidden;
        `,
    },
    stickyNav: {
        css: css`
            position: sticky;
            z-index: 7;
            background-color: white;
            border-top: 1px solid #e8e8e8;
            box-shadow: 0 7px 15px rgba(0, 0, 0, 0.1);
            width: 100%;
            display: flex;
            justify-content: center;
            ${({ theme }: { theme: BaseTheme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        top: var(--header-bottom);
                    `,
                    css`
                        top: var(--header-bottom);
                    `,
                    css`
                        top: var(--header-bottom);
                    `,
                    css`
                        top: var(--header-bottom);
                    `,
                    css`
                        top: var(--header-bottom);
                    `,
                ]);
            }}
        `,
    },
};
const styles = homePageStyles;

const mapStateToProps = (state: ApplicationState) => {
    const categoriesDataView = getCategoriesDataView(state);
    return {
        categoriesDataView: getCategoriesDataView(state),
        topLevelCategoryIds: getSubCategoryIds(state, emptyGuid),
        shouldLoadCategories: !categoriesDataView.value && !categoriesDataView.isLoading,
        location: getLocation(state),
    };
};

const mapDispatchToProps = {
    loadCategories,
};

type Props = PageProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

const HomePage = (props: Props) => {
    const { id, shouldLoadCategories, loadCategories, location, categoriesDataView, topLevelCategoryIds } = props;
    if (shouldLoadCategories) {
        loadCategories();
    }
    if (!categoriesDataView.value || !topLevelCategoryIds) {
        return null;
    }
    const navigationLinksAndId: mappedAnchorLink[] = categoriesDataView.value
        .filter(o => topLevelCategoryIds.indexOf(o.id) > -1)
        .filter(o => o.subCategoryIds !== undefined && o.subCategoryIds.length)
        .map((category, index) => {
            return {
                contentName: category.shortDescription,
                contentID: category.urlSegment,
                active: true,
                sortOrder: index,
            };
        });

    return (
        <Page {...styles.page} data-test-selector="homePage">
            <Zone contentId={id} zoneName="Banner" />
            <StyledWrapper {...styles.stickyNav}>
                <AnchorNavigation navigationItems={navigationLinksAndId} />
            </StyledWrapper>
            <GridContainer {...styles.gridContainerStyle}>
                <GridItem width={12}>
                    <PageBreadcrumbs />
                </GridItem>
            </GridContainer>
            <Zone contentId={id} zoneName="Content" requireRows />
            <Modals />
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(HomePage),
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "System",
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            HideHeader,
            HideFooter,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
        ],
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "HomePage" instead of this constant.
 */
export const HomePageContext = "HomePage";
